import React, { createContext, useContext, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import {
  login as authLogin,
  register as authRegister,
  logout as authLogout,
  getCurrentUser,
  hasValidSession,
  startTokenRefreshInterval,
  stopTokenRefreshInterval,
  UserProfile,
  UserType,
} from '../services/authService';
import { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  login: async () => false,
  logout: async () => {},
  register: async () => false,
  isAuthenticated: false,
  hasRole: () => false,
  hasPermission: () => false,
  isSuperAdmin: false,
  refreshSession: async () => {},
  applyUserProfile: () => {},
});

/**
 * Convert UserProfile from API to User type used in the app
 */
function profileToUser(profile: UserProfile): User {
  return {
    user_id: profile.user_id,
    email: profile.email,
    name: profile.name,
    company: profile.company,
    user_type: profile.user_type as User['user_type'],
    roles: profile.roles,
    permissions: profile.permissions,
    is_superadmin: profile.is_superadmin,
    created_at: profile.created_at,
    last_login_at: profile.last_login_at,
    is_active: profile.is_active,
    terms_accepted_at: profile.terms_accepted_at,
    terms_version: profile.terms_version,
  };
}

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch user profile from backend
   */
  const fetchUserProfile = useCallback(async (): Promise<User | null> => {
    try {
      const profile = await getCurrentUser();
      if (profile) {
        return profileToUser(profile);
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      return null;
    }
  }, []);

  /**
   * Refresh session and update user data
   */
  const refreshSession = useCallback(async () => {
    try {
      if (hasValidSession()) {
        const userData = await fetchUserProfile();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      setUser(null);
    }
  }, [fetchUserProfile]);

  /**
   * Immediately apply a profile payload to auth state (e.g. after accept-terms).
   * Avoids navigating before React has re-rendered with updated consent fields.
   */
  const applyUserProfile = useCallback((profile: UserProfile) => {
    setUser(profileToUser(profile));
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    console.log('[Auth] Logging out user');
    try {
      await authLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    stopTokenRefreshInterval();
    setUser(null);
    // Redirect to home page
    window.location.href = '/';
  }, []);

  /**
   * Check session on mount
   */
  useEffect(() => {
    const checkSession = async () => {
      setIsLoading(true);
      try {
        if (hasValidSession()) {
          const userData = await fetchUserProfile();
          setUser(userData);
          
          // Start token refresh interval if we have a valid session
          if (userData) {
            startTokenRefreshInterval();
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Cleanup on unmount
    return () => {
      stopTokenRefreshInterval();
    };
  }, [fetchUserProfile]);

  /**
   * Login with email and password
   */
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      await authLogin({ email, password });
      
      // Fetch user profile after successful login
      const userData = await fetchUserProfile();
      setUser(userData);
      
      // Start token refresh interval
      if (userData) {
        startTokenRefreshInterval();
      }
      
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  /**
   * Register a new user
   */
  const register = async (
    email: string,
    password: string,
    name: string,
    company?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      await authRegister({
        email,
        password,
        name,
        company: company || '',
        user_type: UserType.PUBLIC, // Default to public user
      });
      
      // Fetch user profile after successful registration
      const userData = await fetchUserProfile();
      setUser(userData);
      
      // Start token refresh interval
      if (userData) {
        startTokenRefreshInterval();
      }
      
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      setIsLoading(false);
      return false;
    }
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((role: string): boolean => {
    if (!user) return false;
    if (user.is_superadmin) return true;
    return user.roles.includes(role);
  }, [user]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    if (user.is_superadmin) return true;
    return user.permissions.includes(permission);
  }, [user]);

  // Check if user is superadmin - check both is_superadmin flag AND user_type
  const isSuperAdminUser = user?.is_superadmin || user?.user_type === 'superadmin';
  
  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    register,
    isAuthenticated: !!user,
    hasRole,
    hasPermission,
    isSuperAdmin: isSuperAdminUser,
    refreshSession,
    applyUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
